import { usePostHog } from 'posthog-js/react';
import { useInitUserOnboard } from 'hooks/useInitUserOnboard';
import {
  useFirebaseAnalytics,
  ANALYTICS_EVENTS,
  logEvent,
} from 'shared/analytics';
import { useEffect } from 'react';
import { SplashScreen } from '@capacitor/splash-screen';
import { useInboxMessages } from 'features/inboxMessages/hooks/useInboxMessages';
import { useAppInfo } from 'hooks/useAppInfo';
import { useOnboardingState } from 'state/useOnboardingState';
import { OnboardingWalkMe } from 'components/OnboardingWalkMe';
import { useLocation } from 'react-router-dom';
import { useSelectedLocationState } from 'state/useSelectedLocationState';
import { useIapInitializer } from 'features/payments/hooks/useIapInitializer';
import { useReconcileInterruptedIap } from 'features/payments/hooks/useReconcileInterruptedIap';
import { useFinishStaleIapTransactions } from 'features/payments/hooks/useFinishStaleIapTransactions';
import { AlertCameraPlayerProvider } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { useSyncAuthUser } from 'hooks/useSyncAuthUser';
import { useMigrateLocalPlaces } from 'features/places/hooks/usePlaces';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Button, Link } from '@mui/material';
import mapboxglSupported from '@mapbox/mapbox-gl-supported';
import { CriticalAlertsDialog } from 'components/CriticalAlertsDialog';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { Routes } from './Routes';

const NotSupportedSnackbarAction = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Button
      component={Link}
      size="small"
      variant="watchduty-text-uppercase"
      href="https://support.watchduty.org/hc/en-us/articles/36184204332045"
      target="_blank"
    >
      {t('map.notSupported.learnMore')}
    </Button>
  );
};

export const Navigation = (): JSX.Element => {
  const { onboardCompleted } = useOnboardingState();
  const posthog = usePostHog();
  useFirebaseAnalytics();
  useIapInitializer();
  useReconcileInterruptedIap();
  useFinishStaleIapTransactions();
  useInitUserOnboard();
  useAppInfo(true);
  useInboxMessages();
  const location = useLocation();
  const { setSelectedLocation } = useSelectedLocationState();
  const { t } = useTranslation();
  useSyncAuthUser();
  useMigrateLocalPlaces();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  // Omit location.search since URL params often modify display of the same page.
  // We further clean these paths from various origins in PostHog settings:
  // https://us.posthog.com/project/172644/settings/project-product-analytics
  const posthogPath = window.location.origin + window.location.pathname;

  useEffect(() => {
    // Delay the Mapbox support check to give the iOS WebView time to fully initialize.
    // Without this delay, mapboxgl.supported() may return a false negative, especially
    // when launching the app via deep links, since the WebGL context might not be ready yet.
    const delayMs = 1000;
    const timeoutId = setTimeout(() => {
      if (!mapboxglSupported.supported()) {
        const notSupportedReason = mapboxglSupported.notSupportedReason();
        posthog.capture('mapbox_not_supported', {
          notSupportedReason,
        });
        logEvent({
          name: ANALYTICS_EVENTS.SUPPORT.MAPBOX_NOT_SUPPORTED,
          params: { notSupportedReason },
        });
        enqueueSnackbar(t('map.notSupported.message'), {
          variant: 'error',
          persist: true,
          action: NotSupportedSnackbarAction,
        });
      }
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [t, posthog]);

  useEffect(() => {
    // If we've completed the onboarding once - we can immediately hide the splash screen
    // otherwise, let the the logic in useInitUserOnboard handle it
    const hideSplashScreen = async (): Promise<void> => {
      await SplashScreen.hide();
    };
    if (onboardCompleted) {
      hideSplashScreen();
    }
  }, [onboardCompleted]);

  useEffect(() => {
    if (!pinImprovements) {
      setSelectedLocation(null);
    }
  }, [location, setSelectedLocation, pinImprovements]);

  useEffect(() => {
    // explicitly handle $pageview events for posthog
    posthog.capture('$pageview', {
      $current_url: posthogPath,
    });
  }, [posthog, posthogPath]);

  return (
    <>
      <AlertCameraPlayerProvider>
        <Routes />
      </AlertCameraPlayerProvider>
      <OnboardingWalkMe />
      <CriticalAlertsDialog />
    </>
  );
};
